import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  renameTeamInput,
  replaceTeamSeatInput,
  type AdminTeamDetail,
  type AdminTeamMember,
  type RenameTeamInput,
  type ReplaceTeamSeatInput,
} from '@gg/shared';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  SelectField,
  Table,
  Td,
  TextField,
  Th,
} from '@/components/ui';
import { adminApiErrorKey } from '@/lib/adminErrors';
import {
  documentStatusTone,
  registrationStatusTone,
  teamMemberStatusTone,
  teamStatusTone,
} from '@/lib/adminTones';
import { formatDateTime } from '@/lib/dates';
import {
  useAdminTeam,
  useAdminTeams,
  useMoveTeamMember,
  useRenameTeam,
  useReplaceTeamSeat,
  useResendTeamSeat,
  useSetTeamCancelled,
  useSetTeamCaptain,
} from '@/lib/queries';
import { teamMembersLabel } from '@/lib/teamLabels';

function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-right font-semibold break-words">{children}</dd>
    </div>
  );
}

const personName = (m: { firstName: string | null; lastName: string | null }) =>
  [m.firstName, m.lastName].filter(Boolean).join(' ');

const isLive = (m: AdminTeamMember) => m.status === 'joined' || m.status === 'invited';

/**
 * A-12: one team with its live seats and its history (dead seats); the rename, the captain
 * appointment, the resend or replacement of an unpaid seat, moving a paid member to another
 * team of the format, and cancelling or reopening the team (records 0023, 0024). Removing a
 * paid member is a refund in Stripe: each paid seat links to its payment.
 */
export function AdminTeam() {
  const { t } = useTranslation();
  const { id } = useParams();
  const team = useAdminTeam(id);
  const setCaptain = useSetTeamCaptain(id ?? '');
  const resend = useResendTeamSeat(id ?? '');
  const [candidate, setCandidate] = useState<AdminTeamMember | null>(null);
  const [replacing, setReplacing] = useState<AdminTeamMember | null>(null);
  const [moving, setMoving] = useState<AdminTeamMember | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const setCancelled = useSetTeamCancelled(id ?? '');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (team.isLoading) return <p className="text-center text-ink-muted">…</p>;
  if (team.isError || !team.data) {
    return (
      <section className="space-y-4">
        <Link to="/admin/teams">{t('admin.team.back')}</Link>
        <p role="alert" className="text-danger">
          {t('admin.team.error')}
        </p>
      </section>
    );
  }
  const d = team.data;
  const none = t('admin.none');
  const live = d.members.filter(isLive);
  const history = d.members.filter((m) => !isLive(m));
  // Invitations go out in the captain's name: none while the team has no captain or is cancelled.
  const canInvite = d.status !== 'cancelled' && d.captain !== null;

  const appoint = async () => {
    if (!candidate) return;
    setError(null);
    try {
      await setCaptain.mutateAsync({ memberId: candidate.id });
      setCandidate(null);
    } catch (err) {
      setError(t(`admin.error.${adminApiErrorKey(err)}`));
    }
  };

  const toggleCancelled = async () => {
    setError(null);
    setNotice(null);
    try {
      await setCancelled.mutateAsync(d.status !== 'cancelled');
    } catch (err) {
      setError(t(`admin.error.${adminApiErrorKey(err)}`));
    }
    setConfirmCancel(false);
  };

  const resendTo = async (m: AdminTeamMember) => {
    setError(null);
    setNotice(null);
    try {
      await resend.mutateAsync(m.id);
      setNotice(t('admin.team.resent', { email: m.invitedEmail }));
    } catch (err) {
      setError(t(`admin.error.${adminApiErrorKey(err)}`));
    }
  };

  return (
    <section className="space-y-6">
      <Link to="/admin/teams" className="text-sm">
        {t('admin.team.back')}
      </Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-section-lg font-bold">{d.name}</h1>
        <Badge tone={teamStatusTone(d.status)}>{t(`admin.teamStatus.${d.status}`)}</Badge>
        {!d.captain && <Badge tone="danger">{t('admin.teams.noCaptain')}</Badge>}
        <Button
          type="button"
          variant="secondary"
          size="md"
          className="ml-auto w-auto!"
          onClick={() => setConfirmCancel(true)}
        >
          {t(d.status === 'cancelled' ? 'admin.team.reopen' : 'admin.team.cancel')}
        </Button>
      </div>

      <Card>
        <h2 className="text-sm font-bold">{t('admin.team.summary')}</h2>
        <dl className="space-y-1.5">
          <Row label={t('admin.teams.col.format')}>{d.formatName}</Row>
          <Row label={t('admin.teams.col.captain')}>
            {d.captain ? `${personName(d.captain)} ${d.captain.email}`.trim() : none}
          </Row>
          <Row label={t('admin.teams.col.members')}>{teamMembersLabel(t, d)}</Row>
          <Row label={t('admin.teams.col.created')}>{formatDateTime(d.createdAt)}</Row>
        </dl>
      </Card>

      <RenameForm team={d} />

      {!d.captain && <p className="text-sm text-ink-muted">{t('admin.team.noCaptainHint')}</p>}
      <p className="text-sm text-ink-muted">{t('admin.team.paidHint')}</p>
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="text-sm text-success">
          {notice}
        </p>
      )}

      <Seats
        members={live}
        renderActions={(m) => (
          <div className="flex flex-wrap justify-end gap-2">
            {m.status === 'joined' && (
              <Button
                type="button"
                variant="ghost"
                size="md"
                className="w-auto!"
                onClick={() => {
                  setError(null);
                  setNotice(null);
                  setMoving(m);
                }}
              >
                {t('admin.team.move')}
              </Button>
            )}
            {m.status === 'joined' && !m.isCaptain && d.status !== 'cancelled' && (
              <Button
                type="button"
                variant="ghost"
                size="md"
                className="w-auto!"
                onClick={() => setCandidate(m)}
              >
                {t('admin.team.makeCaptain')}
              </Button>
            )}
            {m.status === 'invited' && canInvite && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  className="w-auto!"
                  loading={resend.isPending && resend.variables === m.id}
                  onClick={() => resendTo(m)}
                >
                  {t('admin.team.resend')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  className="w-auto!"
                  onClick={() => {
                    setError(null);
                    setNotice(null);
                    setReplacing(m);
                  }}
                >
                  {t('admin.team.replace')}
                </Button>
              </>
            )}
          </div>
        )}
      />

      {history.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-bold">{t('admin.team.history')}</h2>
          <Seats members={history} muted />
        </div>
      )}

      <ConfirmDialog
        open={candidate !== null}
        title={t('admin.team.makeCaptainTitle', { email: candidate?.email ?? '' })}
        confirmLabel={t('admin.team.makeCaptainConfirm')}
        cancelLabel={t('admin.confirm.cancel')}
        onConfirm={appoint}
        onCancel={() => setCandidate(null)}
        loading={setCaptain.isPending}
      >
        <p className="m-0 text-sm">{t('admin.team.makeCaptainBody')}</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmCancel}
        title={t(d.status === 'cancelled' ? 'admin.team.reopenTitle' : 'admin.team.cancelTitle', {
          name: d.name,
        })}
        confirmLabel={t(
          d.status === 'cancelled' ? 'admin.team.reopenConfirm' : 'admin.team.cancelConfirm',
        )}
        cancelLabel={t(d.status === 'cancelled' ? 'admin.confirm.cancel' : 'admin.team.keepTeam')}
        onConfirm={toggleCancelled}
        onCancel={() => setConfirmCancel(false)}
        loading={setCancelled.isPending}
      >
        <p className="m-0 text-sm">
          {t(d.status === 'cancelled' ? 'admin.team.reopenBody' : 'admin.team.cancelBody', {
            count: d.invited,
          })}
        </p>
      </ConfirmDialog>

      {moving && (
        <MoveDialog
          team={d}
          seat={moving}
          onDone={(name) => {
            setMoving(null);
            setNotice(t('admin.team.moved', { email: moving.email, name }));
          }}
          onCancel={() => setMoving(null)}
        />
      )}

      {replacing && (
        <ReplaceDialog
          teamId={d.id}
          seat={replacing}
          onDone={(email) => {
            setReplacing(null);
            setNotice(t('admin.team.replaced', { email }));
          }}
          onCancel={() => setReplacing(null)}
        />
      )}
    </section>
  );
}

/** Seats table: live seats with their actions, or the history (dead seats, muted). */
function Seats({
  members,
  muted = false,
  renderActions,
}: {
  members: AdminTeamMember[];
  muted?: boolean;
  renderActions?: (m: AdminTeamMember) => ReactNode;
}) {
  const { t } = useTranslation();
  const none = t('admin.none');
  return (
    <Table>
      <thead>
        <tr>
          <Th>{t('admin.team.col.email')}</Th>
          <Th>{t('admin.team.col.name')}</Th>
          <Th>{t('admin.team.col.status')}</Th>
          <Th>{t('admin.team.col.payment')}</Th>
          <Th>{t('admin.team.col.documents')}</Th>
          <Th>{t('admin.team.col.invitedAt')}</Th>
          <Th>{muted ? t('admin.team.col.endedAt') : t('admin.team.col.joinedAt')}</Th>
          {renderActions && <Th />}
        </tr>
      </thead>
      <tbody>
        {members.map((m) => (
          <tr key={m.id} className={muted ? 'text-ink-subtle' : ''}>
            <Td className="font-semibold">
              {m.email}
              {m.isCaptain && (
                <span className="ml-2">
                  <Badge tone="accent">{t('admin.team.captainBadge')}</Badge>
                </span>
              )}
              {m.invitedEmail.toLowerCase() !== m.email.toLowerCase() && (
                <span className="block text-helper font-normal text-ink-muted">
                  {t('admin.team.invitedAs', { email: m.invitedEmail })}
                </span>
              )}
            </Td>
            <Td>{personName(m) || none}</Td>
            <Td>
              <Badge tone={teamMemberStatusTone(m.status)}>
                {t(`admin.team.memberStatus.${m.status}`)}
              </Badge>
            </Td>
            <Td>
              {m.registration ? (
                <span className="flex flex-wrap items-center gap-2">
                  <Link to={`/admin/registrations/${m.registration.id}`}>
                    {t('admin.team.registration')}
                  </Link>
                  <Badge tone={registrationStatusTone(m.registration.status)}>
                    {t(`admin.status.${m.registration.status}`)}
                  </Badge>
                  <Badge tone={m.registration.insured ? 'success' : 'neutral'}>
                    {t(m.registration.insured ? 'admin.insured.yes' : 'admin.insured.no')}
                  </Badge>
                  {m.registration.stripeUrl && (
                    <a href={m.registration.stripeUrl} target="_blank" rel="noreferrer">
                      {t('admin.orders.stripe')}
                    </a>
                  )}
                </span>
              ) : (
                none
              )}
            </Td>
            <Td>
              {m.registration && m.status === 'joined' ? (
                <Badge tone={documentStatusTone(m.registration.documentStatus)}>
                  {t(`admin.documents.${m.registration.documentStatus}`)}
                </Badge>
              ) : (
                none
              )}
            </Td>
            <Td className="whitespace-nowrap text-ink-muted">{formatDateTime(m.invitedAt)}</Td>
            <Td className="whitespace-nowrap text-ink-muted">
              {muted
                ? m.replacedAt
                  ? formatDateTime(m.replacedAt)
                  : none
                : m.joinedAt
                  ? formatDateTime(m.joinedAt)
                  : none}
            </Td>
            {renderActions && <Td className="text-right">{renderActions(m)}</Td>}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/** Rename (record 0021 D22: the back office only), same rule as the checkout team name. */
function RenameForm({ team }: { team: AdminTeamDetail }) {
  const { t } = useTranslation();
  const rename = useRenameTeam(team.id);
  const [saved, setSaved] = useState(false);
  const { register, handleSubmit, formState, setError } = useForm<RenameTeamInput>({
    resolver: zodResolver(renameTeamInput),
    values: { name: team.name },
  });

  const onSubmit = async (v: RenameTeamInput) => {
    setSaved(false);
    try {
      await rename.mutateAsync(v);
      setSaved(true);
    } catch (err) {
      setError('name', { type: 'manual', message: t(`admin.error.${adminApiErrorKey(err)}`) });
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-wrap items-end gap-3"
      noValidate
      aria-label={t('admin.team.rename.title')}
    >
      <div className="min-w-64 flex-1">
        <TextField
          label={t('admin.team.rename.label')}
          error={
            formState.errors.name &&
            (formState.errors.name.type === 'manual'
              ? formState.errors.name.message
              : t('admin.team.rename.invalid'))
          }
          hint={saved ? t('admin.team.rename.saved') : t('admin.team.rename.hint')}
          hintTone={saved ? 'success' : 'muted'}
          {...register('name', { onChange: () => setSaved(false) })}
        />
      </div>
      <Button
        type="submit"
        variant="secondary"
        size="md"
        className="mb-6 w-auto!"
        loading={rename.isPending}
        disabled={!formState.isDirty}
      >
        {t('admin.team.rename.submit')}
      </Button>
    </form>
  );
}

/**
 * Replace an unpaid seat on request (the captain's replace, then a new invitation): confirmed,
 * since the old link stops working at once.
 */
function ReplaceDialog({
  teamId,
  seat,
  onDone,
  onCancel,
}: {
  teamId: string;
  seat: AdminTeamMember;
  onDone: (email: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const replace = useReplaceTeamSeat(teamId);
  const { register, handleSubmit, formState, setError } = useForm<ReplaceTeamSeatInput>({
    resolver: zodResolver(replaceTeamSeatInput),
    defaultValues: { email: '' },
  });

  const submit = handleSubmit(async (v) => {
    try {
      await replace.mutateAsync({ memberId: seat.id, body: v });
      onDone(v.email);
    } catch (err) {
      setError('email', {
        type: 'manual',
        message: t(`admin.error.${adminApiErrorKey(err)}`),
      });
    }
  });

  return (
    <ConfirmDialog
      open
      title={t('admin.team.replaceTitle', { email: seat.invitedEmail })}
      confirmLabel={t('admin.team.replaceConfirm')}
      cancelLabel={t('admin.confirm.cancel')}
      onConfirm={() => void submit()}
      onCancel={onCancel}
      loading={replace.isPending}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        noValidate
        className="space-y-3"
      >
        <p className="m-0 text-sm">{t('admin.team.replaceBody')}</p>
        <TextField
          label={t('admin.team.replaceEmail')}
          type="email"
          autoComplete="off"
          autoFocus
          error={
            formState.errors.email &&
            (formState.errors.email.type === 'manual'
              ? formState.errors.email.message
              : t('admin.team.replaceInvalid'))
          }
          {...register('email')}
        />
      </form>
    </ConfirmDialog>
  );
}

/**
 * Move a paid member to another team of the same format (record 0024 Q2): confirmed, with the
 * team picked among the format's teams that are not cancelled. The API checks the seats left.
 */
function MoveDialog({
  team,
  seat,
  onDone,
  onCancel,
}: {
  team: AdminTeamDetail;
  seat: AdminTeamMember;
  onDone: (teamName: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const move = useMoveTeamMember(team.id);
  const list = useAdminTeams({ formatId: team.formatId });
  const targets = (list.data?.teams ?? []).filter(
    (x) => x.id !== team.id && x.status !== 'cancelled',
  );
  const [targetId, setTargetId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const target = targets.find((x) => x.id === targetId);
    if (!target) {
      setError(t('admin.team.moveChoose'));
      return;
    }
    setError(null);
    try {
      await move.mutateAsync({ memberId: seat.id, body: { teamId: target.id } });
      onDone(target.name);
    } catch (err) {
      setError(t(`admin.error.${adminApiErrorKey(err)}`));
    }
  };

  return (
    <ConfirmDialog
      open
      title={t('admin.team.moveTitle', { email: seat.email })}
      confirmLabel={t('admin.team.moveConfirm')}
      cancelLabel={t('admin.confirm.cancel')}
      onConfirm={() => void submit()}
      onCancel={onCancel}
      loading={move.isPending}
    >
      <div className="space-y-3">
        <p className="m-0 text-sm">
          {t(seat.isCaptain ? 'admin.team.moveBodyCaptain' : 'admin.team.moveBody')}
        </p>
        {list.data && targets.length === 0 ? (
          <p className="m-0 text-sm text-ink-muted">{t('admin.team.moveNone')}</p>
        ) : (
          <SelectField
            label={t('admin.team.moveTarget')}
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
          >
            <option value="">{t('admin.team.moveChoose')}</option>
            {targets.map((x) => (
              <option key={x.id} value={x.id}>
                {`${x.name} · ${teamMembersLabel(t, x)}`}
              </option>
            ))}
          </SelectField>
        )}
        {error && (
          <p role="alert" className="m-0 text-helper text-danger">
            {error}
          </p>
        )}
      </div>
    </ConfirmDialog>
  );
}
